<template>
  <DefaultLayout>
    <main class="flex-grow">
      <ImportantAnnouncements />

      <div class="container mx-auto px-4 mt-8 pb-32">
        <!-- Page Title -->
        <div class="mb-10">
          <div class="text-center mx-auto mt-4">
            <img
              :src="images.customerService"
              alt="Customer Service"
              class="h-48 md:h-56 mx-auto"
              loading="lazy"
            />
          </div>
          <h1
            class="text-center align-middle text-2xl md:text-4xl font-bold text-ietextlight mb-4"
            id="customerServiceHeading"
          >
            Customer Service
            <span class="inline-block items-center" aria-hidden="true"
              ><Icon
                icon="line-md:heart-filled"
                class="text-red-600 text-2xl md:text-4xl"
            /></span>
          </h1>
          <p class="text-center text-md md:text-xl text-gray-600 max-w-2xl mx-auto">
            Welcome to our customer service portal. Please select the appropriate option below based on your role.
          </p>
        </div>

        <!-- Search functionality -->
        <div class="max-w-lg mx-auto mb-12">
          <div class="relative">
            <label for="service-search" class="sr-only">Search for services</label>
            <input 
              type="text" 
              id="service-search"
              v-model="searchQuery" 
              placeholder="Search for services..." 
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Search for customer service options"
              aria-controls="service-cards"
            />
            <button 
              class="absolute right-3 top-1/2 transform -translate-y-1/2"
              aria-label="Clear search"
              v-if="searchQuery"
              @click="searchQuery = ''"
            >
              <Icon icon="mdi:close" class="text-gray-500" />
            </button>
          </div>
          <p class="text-sm text-gray-500 mt-2" v-if="filteredCards.length === 0 && searchQuery" aria-live="polite">
            No services found matching "{{ searchQuery }}". Please try different keywords.
          </p>
        </div>

        <!-- User Type Selection - More subtle styling -->
        <div class="flex flex-col lg:flex-row gap-6 mb-12">
          <!-- Customer Portal Entry - More subtle styling -->
          <div 
            class="w-full lg:w-1/2 rounded-lg overflow-hidden shadow-md border border-gray-200"
          >
            <div class="bg-customer-secondary p-6 border-t-4 border-customer">
              <div class="flex items-center justify-between">
                <h2 class="text-2xl font-bold text-customer">For Insurance Customers</h2>
                <Icon icon="mdi:account" class="text-customer text-3xl" />
              </div>
              <p class="mt-2 text-gray-700">Policy owners and individuals requiring service</p>
            </div>
            <div class="bg-white p-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                <!-- Policy Changes -->
                <div
                  v-if="isCardVisible('Policy Changes', 'Make service requests for your policy through our web site.')"
                  class="card-customer p-0.5 rounded-lg flex flex-col justify-between hover:shadow-md transition-shadow"
                >
                  <div class="flex flex-col items-center">
                    <div class="w-32 h-32 rounded-full bg-customer-secondary flex items-center justify-center -mb-1">
                      <img
                        :src="images.policyChanges"
                        alt="Policy Changes"
                        class="h-20 w-20"
                        loading="lazy"
                      />
                    </div>
                    <h3 class="font-semibold text-center">Policy Changes</h3>
                  </div>
                  <p class="text-gray-600 text-sm mb-2 text-center">
                    Update addresses, add vehicles, and make other changes to your policy
                  </p>
                  <router-link
                    :to="{ name: 'customerService.policyChanges' }"
                    class="btn-customer text-sm py-1.5"
                  >
                    Make Changes
                  </router-link>
                </div>
                
                <!-- Make a Payment -->
                <div
                  v-if="isCardVisible('Make a Payment', 'Pay your premium or set up automatic payments')"
                  class="card-customer p-0.5 rounded-lg flex flex-col justify-between hover:shadow-md transition-shadow"
                >
                  <div class="flex flex-col items-center">
                    <div class="w-32 h-32 rounded-full bg-customer-secondary flex items-center justify-center -mb-1">
                      <img
                        :src="images.makeAPayment"
                        alt="Make a Payment"
                        class="h-20 w-20"
                        loading="lazy"
                      />
                    </div>
                    <h3 class="font-semibold text-center">Make a Payment</h3>
                  </div>
                  <p class="text-gray-600 text-sm mb-2 text-center">
                    Pay your premium or set up automatic payments
                  </p>
                  <router-link
                    :to="`/customerservice/carrier-directory/make-payment`"
                    class="btn-customer text-sm py-1.5"
                  >
                    Pay Now
                  </router-link>
                </div>
                
                <!-- File a Claim -->
                <div
                  v-if="isCardVisible('Claims', 'Use our directory to find your carrier and make claims through carrier claims links.')"
                  class="card-customer p-0.5 rounded-lg flex flex-col justify-between hover:shadow-md transition-shadow"
                >
                  <div class="flex flex-col items-center">
                    <div class="w-32 h-32 rounded-full bg-customer-secondary flex items-center justify-center -mb-1">
                      <img
                        :src="images.claims"
                        alt="Make a Claim"
                        class="h-20 w-20"
                        loading="lazy"
                      />
                    </div>
                    <h3 class="font-semibold text-center">File a Claim</h3>
                  </div>
                  <p class="text-gray-600 text-sm mb-2 text-center">
                    Report damage and start the claims process with your carrier
                  </p>
                  <router-link
                    :to="`/customerservice/carrier-directory/make-claim`"
                    class="btn-customer text-sm py-1.5"
                  >
                    Start Claim
                  </router-link>
                </div>
                
                <!-- Chat Support -->
                <div
                  v-if="isCardVisible('Live Chat Support', 'Instant support at your fingertips. Chat with us now for quick answers and policy assistance.')"
                  class="card-customer p-0.5 rounded-lg flex flex-col justify-between hover:shadow-md transition-shadow relative"
                >
                  <!-- More subtle recommended badge -->
                  <div class="absolute -top-1 -right-1 bg-green-100 text-green-800 text-xs py-0.5 px-2 rounded-full border border-green-300">
                    Recommended
                  </div>
                  <div class="flex flex-col items-center">
                    <div class="w-32 h-32 rounded-full bg-customer-secondary flex items-center justify-center -mb-1">
                      <img
                        :src="images.liveChat"
                        alt="Live Chat Support"
                        class="h-20 w-20"
                        loading="lazy"
                      />
                    </div>
                    <h3 class="font-semibold text-center">Live Chat Support</h3>
                  </div>
                  <p class="text-gray-600 text-sm mb-2 text-center">
                    Speak with a representative now for quick assistance
                  </p>
                  <button
                    @click="openChatWidget"
                    class="btn-customer text-sm py-1.5"
                  >
                    Chat Now
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Lender Portal Entry - More subtle styling -->
          <div 
            class="w-full lg:w-1/2 rounded-lg overflow-hidden shadow-md border border-gray-200"
          >
            <div class="bg-lender-secondary p-6 border-t-4 border-lender">
              <div class="flex items-center justify-between">
                <h2 class="text-2xl font-bold text-lender">For Lenders & Mortgagees</h2>
                <Icon icon="mdi:bank" class="text-lender text-3xl" />
              </div>
              <p class="mt-2 text-gray-700">Banking institutions, mortgage companies, and other lenders</p>
            </div>
            <div class="bg-white p-6">
              <div class="flex flex-col space-y-4">
                <div
                  v-if="isCardVisible('Mortgagee Changes', 'Quick and easy requests for lenders to submit policy changes or receive proof of insurance')"
                  class="card-lender p-5 rounded-lg hover:shadow-md transition-shadow"
                >
                  <div class="flex items-center mb-3">
                    <div class="w-12 h-12 rounded-full bg-lender-secondary flex items-center justify-center mr-4">
                      <img
                        :src="images.mortgageeChanges"
                        alt="Mortgagee Changes"
                        class="h-8 w-8"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <h3 class="font-semibold text-lg">Lender Service Portal</h3>
                      <p class="text-gray-600">
                        Request mortgagee changes, proof of insurance, and coverage updates
                      </p>
                    </div>
                  </div>
                  <div class="border-t pt-3 flex justify-end">
                    <router-link
                      :to="{ name: 'customerService.lenderRequests' }"
                      class="btn-lender"
                    >
                      Access Lender Portal
                    </router-link>
                  </div>
                </div>
                
                <!-- More subtle info box -->
                <div class="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <div class="flex items-start">
                    <Icon icon="mdi:information" class="text-lender text-xl mr-2 mt-0.5" />
                    <p class="text-sm text-gray-700">
                      The lender portal provides secure access to policy information for mortgagees and banking institutions. If you're a policyholder rather than a lender, please use the customer options on the left.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Additional Resources Section -->
        <div class="bg-gray-50 rounded-lg p-6 shadow-sm">
          <h2 class="text-xl font-semibold mb-4 text-center">Additional Resources</h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Citizens Insurance -->
            <div
              v-if="isCardVisible('Citizens Insurance Policies', 'Information and support for policies with Citizens Insurance.')"
              class="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <img
                :src="images.citizensLogo"
                alt="Citizens Insurance Logo"
                class="h-12 mb-3 mx-auto"
                loading="lazy"
              />
              <h3 class="text-center font-medium mb-2">Citizens Insurance</h3>
              <p class="text-gray-600 text-sm text-center mb-3">
                Specialized support for Citizens Insurance policyholders
              </p>
              <div class="text-center">
                <router-link
                  :to="{ name: 'customerService.citizens' }"
                  class="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Learn More
                </router-link>
              </div>
            </div>
            
            <!-- Phone Support -->
            <div 
              v-if="isCardVisible('Phone Support', 'Give us a call and speak to our licensed customer service team for assistance at (800) 268-9137')"
              class="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div class="flex justify-center">
                <Icon icon="ic:outline-phone-in-talk" class="text-blue-600 text-3xl mb-3" />
              </div>
              <h3 class="text-center font-medium mb-2">Phone Support</h3>
              <p class="text-gray-600 text-sm text-center mb-3">
                Speak directly with our licensed customer service team
              </p>
              <div class="text-center">
                <a href="tel:8002689137" class="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md transition-colors">
                  (800) 268-9137
                </a>
              </div>
            </div>
            
            <!-- Policy Search -->
            <div
              class="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div class="flex justify-center">
                <Icon icon="mdi:magnify" class="text-blue-600 text-3xl mb-3" />
              </div>
              <h3 class="text-center font-medium mb-2">Policy Search</h3>
              <p class="text-gray-600 text-sm text-center mb-3">
                Find your policy information if you don't have your policy number
              </p>
              <div class="text-center">
                <router-link 
                  :to="{ name: 'customerService.policySearch' }" 
                  class="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Find Policy
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { Icon } from '@iconify/vue';
import ImportantAnnouncements from '../components/ImportantAnnouncements.vue';
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';

// Import customer service page images
import customerServiceImg from '@/assets/images/customerService.svg';
import policyChangesIcon from '@/assets/images/ui/icons/policyChanges.svg';
import makeAPaymentIcon from '@/assets/images/ui/icons/makeAPayment.svg';
import claimsIcon from '@/assets/images/ui/icons/claims.svg';
import liveChatIcon from '@/assets/images/ui/icons/liveChat.svg';
import mortgageeChangesIcon from '@/assets/images/ui/icons/mortgageeChanges.svg';
import citizensLogo from '@/assets/images/carrierLogos/citizens-prop-1.svg';

export default {
  name: 'CustomerServicePage',
  components: {
    DefaultLayout,
    Icon,
    ImportantAnnouncements,
  },
  setup() {
    // Define the image paths for use in the template
    const images = {
      customerService: customerServiceImg,
      policyChanges: policyChangesIcon,
      makeAPayment: makeAPaymentIcon,
      claims: claimsIcon,
      liveChat: liveChatIcon,
      mortgageeChanges: mortgageeChangesIcon,
      citizensLogo: citizensLogo
    };

    // Reactive property for window width
    const windowWidth = ref(window.innerWidth);

    // Computed property to determine if the screen is mobile size
    const isMobile = computed(() => windowWidth.value < 768);

    // Function to update the window width
    const updateWidth = () => {
      windowWidth.value = window.innerWidth;
    };

    // Mounted lifecycle hook
    onMounted(() => {
      window.addEventListener('resize', updateWidth);
    });

    // BeforeUnmount lifecycle hook
    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateWidth);
    });

    const showTooltip = ref(false);

    const searchQuery = ref('');
    const filteredCards = computed(() => {
      if (!searchQuery.value) return true;
      
      // Implementation would filter the visible cards based on search
      return [];
    });

    const isCardVisible = (title, description) => {
      if (!searchQuery.value) return true;
      
      return (
        title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        description.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    };

    const openChatWidget = () => {
      // Dispatch custom event to trigger the chat widget
      window.dispatchEvent(new CustomEvent('chatwoot:trigger-open', {
        detail: { source: 'chat-now-button' }
      }));
    };

    return { 
      images,
      isMobile, 
      showTooltip, 
      searchQuery, 
      filteredCards, 
      isCardVisible,
      openChatWidget
    };
  },
};
</script>

<style scoped>
/* Card hover effects */
.card-customer:hover, .card-lender:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style>
